import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";
import { useGoogleMapsDistance } from "../../hooks/useGoogleMapsDistance";
import { apiMapsTolls } from "../../services/mapsConfigApi";
import { apiEnviarPropostaEmail } from "../../services/financeiroPropostasEmailApi";
import { blobToBase64, gerarPdfPropostaOs } from "../../services/financeiroPropostasPdfService";

const COLLECTION_PROPOSTAS_OS = "financeiro_propostas_os";
const COLLECTION_SESSOES = "atendimentos_sessoes";
const COLLECTION_ATENDIMENTOS = "atendimentos_preventiva";
const COLLECTION_ITENS = "atendimentos_sessao_itens";
const COLLECTION_EMPRESAS = "empresas";
const COLLECTION_SETORES = "setores";

const STATUS_OPTIONS = [
  { value: "CRIADA", label: "Proposta Criada" },
  { value: "ENVIADA", label: "Enviada" },
  { value: "APROVADA", label: "Aprovada" },
  { value: "REPROVADA", label: "Reprovada" },
  { value: "FINALIZADA", label: "Finalizada" },
];

const FORMAS_PAGAMENTO = [
  "Dinheiro",
  "Pix",
  "Cartao",
  "Boleto",
  "Transferencia",
  "Outro",
];

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseNumero(value) {
  let raw = String(value || "").trim();
  if (!raw) return 0;
  if (raw.includes(".") && raw.includes(",")) {
    raw = raw.replace(".", "").replace(",", ".");
  } else if (raw.includes(",")) {
    raw = raw.replace(",", ".");
  }
  const num = Number.parseFloat(raw);
  return Number.isNaN(num) ? 0 : num;
}

function formatNumero(value, decimals = 2) {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(decimals);
}

function montarEndereco(data) {
  if (!data) return "";
  const parts = [
    data.enderecoCompleto,
    data.endereco,
    [data.logradouro, data.numero].filter(Boolean).join(", "),
    data.bairro,
    data.cidade,
    data.estado,
    data.cep,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  return Array.from(new Set(parts)).join(" - ");
}

function isContratoAvulso(value) {
  return String(value || "").toLowerCase().includes("avulso");
}

function normalizarStatusVisita(sessao) {
  const raw = String(sessao?.status || "").trim().toUpperCase();
  if (raw === "FINALIZADA") return "FINALIZADA";
  if (raw === "CANCELADA") return "CANCELADA";
  if (raw === "ATENDIDA" || raw === "REALIZADA") return "ATENDIDA";
  if (raw === "AGENDADA") return "AGENDADA";
  return "PLANEJADA";
}

export default function FinanceiroOrdemServicoFormPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [clientes, setClientes] = useState([]);
  const [setores, setSetores] = useState([]);
  const [visitas, setVisitas] = useState([]);
  const [osItens, setOsItens] = useState([]);

  const [visitaId, setVisitaId] = useState("");
  const [atendimentoId, setAtendimentoId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [academiaId, setAcademiaId] = useState("");
  const [status, setStatus] = useState("CRIADA");
  const [dataPagamento, setDataPagamento] = useState("");
  const [formaPagamento, setFormaPagamento] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [enderecoDestino, setEnderecoDestino] = useState("");
  const [kmRodados, setKmRodados] = useState("");
  const [kmIda, setKmIda] = useState(0);
  const [kmVolta, setKmVolta] = useState(0);
  const [valorDeslocamento, setValorDeslocamento] = useState("");
  const [valorPedagio, setValorPedagio] = useState("");
  const [pedagioIda, setPedagioIda] = useState(0);
  const [pedagioVolta, setPedagioVolta] = useState(0);
  const [valorMaoObra, setValorMaoObra] = useState("");
  const [valorPecas, setValorPecas] = useState("");
  const [valorDesconto, setValorDesconto] = useState("");
  const [tipoViagem, setTipoViagem] = useState("CARRO");
  const [quantidadeAjudantes, setQuantidadeAjudantes] = useState("");
  const [gastosAlmoco, setGastosAlmoco] = useState("");
  const [previsaoDias, setPrevisaoDias] = useState("");
  const [valorPassagem, setValorPassagem] = useState("");
  const [aluguelCarro, setAluguelCarro] = useState(false);
  const [valorAluguelCarro, setValorAluguelCarro] = useState("");

  const [enderecoOrigem, setEnderecoOrigem] = useState("");
  const [valorKmDeslocamento, setValorKmDeslocamento] = useState(1.2);
  const [idaVolta, setIdaVolta] = useState(true);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [carregandoRegistro, setCarregandoRegistro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [calculandoDistancia, setCalculandoDistancia] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  const isEdicao = !!id;
  const grupoId = usuario?.grupoId || "";

  const { calcularDistancia, gerarUrlGoogleMaps } = useGoogleMapsDistance();

  const clientesById = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[String(c.id)] = c;
    });
    return map;
  }, [clientes]);

  const setoresById = useMemo(() => {
    const map = {};
    setores.forEach((s) => {
      map[String(s.id)] = s;
    });
    return map;
  }, [setores]);

  const academiasFiltradas = useMemo(() => {
    if (!clienteId) return [];
    return setores.filter((s) => String(s.empresaId || "") === String(clienteId));
  }, [setores, clienteId]);

  const visitaSelecionada = useMemo(
    () => visitas.find((v) => String(v.id) === String(visitaId)) || null,
    [visitas, visitaId]
  );

  useEffect(() => {
    const carregarBase = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      try {
        const [snapClientes, snapSetores] = await Promise.all([
          getDocs(query(collection(db, COLLECTION_EMPRESAS), where("grupoId", "==", usuario.grupoId))),
          getDocs(query(collection(db, COLLECTION_SETORES), where("grupoId", "==", usuario.grupoId))),
        ]);

        setClientes(snapClientes.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSetores(snapSetores.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Erro ao carregar base:", e);
        setErro("Erro ao carregar dados base.");
      } finally {
        setCarregando(false);
      }
    };

    carregarBase();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarEnderecoGrupo = async () => {
      if (!usuario?.grupoId) return;
      try {
        const grupoRef = doc(db, "grupos", usuario.grupoId);
        const grupoSnap = await getDoc(grupoRef);
        if (!grupoSnap.exists()) return;

        const grupoData = grupoSnap.data() || {};
        setEnderecoOrigem(grupoData.enderecoOrigem || "");
        if (grupoData.valorKmDeslocamento != null) {
          const parsed = Number.parseFloat(String(grupoData.valorKmDeslocamento));
          if (!Number.isNaN(parsed)) setValorKmDeslocamento(parsed);
        }
      } catch (e) {
        console.error("Erro ao carregar endereço do grupo:", e);
      }
    };

    carregarEnderecoGrupo();
  }, [usuario?.grupoId]);

  useEffect(() => {
    if (!usuario?.grupoId) return;

    const carregarVisitas = async () => {
      setCarregando(true);
      try {
        const snapSessoes = await getDocs(
          query(
            collection(db, COLLECTION_SESSOES),
            where("grupoId", "==", usuario.grupoId)
          )
        );

        const sessoes = snapSessoes.docs.map((d) => ({ id: d.id, ...d.data() }));
        const atendimentoIds = Array.from(
          new Set(sessoes.map((s) => String(s.atendimentoId || "")).filter(Boolean))
        );

        const atendimentos = await Promise.all(
          atendimentoIds.map(async (atId) => {
            try {
              const snap = await getDoc(doc(db, COLLECTION_ATENDIMENTOS, atId));
              if (snap.exists()) return { id: snap.id, ...snap.data() };
            } catch {
              return null;
            }
            return null;
          })
        );

        const atendimentosById = new Map(atendimentos.filter(Boolean).map((a) => [String(a.id), a]));

        const itensPorSessao = new Map();
        await Promise.all(
          sessoes.map(async (s) => {
            try {
              const atendimento = atendimentosById.get(String(s.atendimentoId || "")) || {};
              const empresaIdSessao = s.empresaId || atendimento.empresaId || null;
              const filtros = [
                where("grupoId", "==", usuario.grupoId),
                where("sessaoId", "==", s.id),
              ];
              if (empresaIdSessao) {
                filtros.push(where("empresaId", "==", empresaIdSessao));
              }

              const snapItens = await getDocs(
                query(collection(db, COLLECTION_ITENS), ...filtros)
              );
              itensPorSessao.set(s.id, snapItens.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch {
              itensPorSessao.set(s.id, []);
            }
          })
        );

        const filtradas = sessoes
          .filter((s) => {
            if (s.propostaId && !isEdicao) return false;
            if (isEdicao && s.propostaId && String(s.propostaId) !== String(id)) return false;

            const status = normalizarStatusVisita(s);
            if (["FINALIZADA", "CANCELADA", "ATENDIDA"].includes(status)) return false;

            const itens = itensPorSessao.get(s.id) || [];
            const temOS = itens.some(
              (item) => String(item?.tipo || "").toUpperCase() === "OS" || !!item?.osId
            );
            return temOS;
          })
          .map((s) => {
            const atendimento = atendimentosById.get(String(s.atendimentoId || "")) || {};
            return {
              ...s,
              atendimento,
              clienteId: atendimento.clienteId || s.clienteId || s.empresaId || null,
              academiaId: atendimento.academiaId || atendimento.setorId || s.academiaId || s.setorId || null,
            };
          });

        setVisitas(filtradas);
      } catch (e) {
        console.error("Erro ao carregar visitas:", e);
        setErro("Erro ao carregar visitas avulsas.");
        setVisitas([]);
      } finally {
        setCarregando(false);
      }
    };

    carregarVisitas();
  }, [usuario?.grupoId, id, isEdicao]);

  useEffect(() => {
    if (!isEdicao || !id) return;

    const carregarRegistro = async () => {
      setCarregandoRegistro(true);
      try {
        const snap = await getDoc(doc(db, COLLECTION_PROPOSTAS_OS, id));
        if (!snap.exists()) {
          setErro("Proposta nao encontrada.");
          return;
        }
        const data = snap.data() || {};
        setVisitaId(data.sessaoId || "");
        setAtendimentoId(data.atendimentoId || "");
        setClienteId(data.clienteId || "");
        setAcademiaId(data.academiaId || "");
        setStatus(data.status || "CRIADA");
        setDataPagamento(data.dataPagamento || "");
        setFormaPagamento(data.formaPagamento || "");
        setObservacoes(data.observacoes || "");
        setOsItens(Array.isArray(data.osResumo) ? data.osResumo : []);

        setEnderecoDestino(data.enderecoDestino || "");
        setKmRodados(String(data.kmRodados ?? ""));
        setValorDeslocamento(String(data.valorDeslocamento ?? ""));
        setValorPedagio(String(data.valorPedagio ?? ""));
        setValorMaoObra(String(data.valorMaoObra ?? ""));
        setValorPecas(String(data.valorPecas ?? ""));
        setValorDesconto(String(data.valorDesconto ?? ""));
        setTipoViagem(data.tipoViagem || "CARRO");
        setQuantidadeAjudantes(String(data.quantidadeAjudantes ?? ""));
        setGastosAlmoco(String(data.gastosAlmoco ?? ""));
        setPrevisaoDias(String(data.previsaoDias ?? ""));
        setValorPassagem(String(data.valorPassagem ?? ""));
        setAluguelCarro(Boolean(data.aluguelCarro));
        setValorAluguelCarro(String(data.valorAluguelCarro ?? ""));
        setKmIda(data.kmIda ?? 0);
        setKmVolta(data.kmVolta ?? 0);
        setPedagioIda(data.pedagioIda ?? 0);
        setPedagioVolta(data.pedagioVolta ?? 0);
        setIdaVolta(data.idaVolta ?? true);
      } catch (e) {
        console.error("Erro ao carregar proposta:", e);
        setErro("Erro ao carregar proposta.");
      } finally {
        setCarregandoRegistro(false);
      }
    };

    carregarRegistro();
  }, [id, isEdicao]);

  useEffect(() => {
    const carregarOsSessao = async () => {
      if (!visitaId) {
        setOsItens([]);
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, COLLECTION_ITENS), where("sessaoId", "==", visitaId)));
        const itens = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const os = itens
          .filter((i) => String(i?.tipo || "").toUpperCase() === "OS" || i?.osId)
          .map((i) => ({
            osId: i.osId || null,
            osCodigo: i.osCodigo || null,
            osTitulo: i.osTitulo || null,
            osTipoServico: i.osTipoServico || null,
          }));
        setOsItens(os);
      } catch (e) {
        console.error("Erro ao carregar OS da sessao:", e);
      }
    };

    carregarOsSessao();
  }, [visitaId]);

  useEffect(() => {
    if (!visitaSelecionada) return;
    setAtendimentoId(visitaSelecionada.atendimentoId || "");
    setClienteId(visitaSelecionada.clienteId || "");
    setAcademiaId(visitaSelecionada.academiaId || "");
  }, [visitaSelecionada]);

  useEffect(() => {
    const setor = setoresById[String(academiaId)] || null;
    if (!setor) return;
    const endereco = montarEndereco(setor);
    if (!endereco) return;
    if (!isEdicao || !enderecoDestino) {
      setEnderecoDestino(endereco);
    }
  }, [academiaId, setoresById, isEdicao, enderecoDestino]);

  useEffect(() => {
    const totalKm = idaVolta ? Number(kmIda || 0) + Number(kmVolta || 0) : Number(kmIda || 0);
    if (totalKm > 0) setKmRodados(totalKm.toString());
  }, [idaVolta, kmIda, kmVolta]);

  useEffect(() => {
    const totalPedagio = idaVolta ? Number(pedagioIda || 0) + Number(pedagioVolta || 0) : Number(pedagioIda || 0);
    setValorPedagio(formatNumero(totalPedagio));
  }, [idaVolta, pedagioIda, pedagioVolta]);

  useEffect(() => {
    if (tipoViagem === "AVIAO") {
      setValorDeslocamento(formatNumero(0));
      return;
    }
    const km = Number(kmRodados || 0);
    setValorDeslocamento(formatNumero(km * valorKmDeslocamento));
  }, [kmRodados, tipoViagem, valorKmDeslocamento]);

  const valorTotalCalculado = useMemo(() => {
    const desloc = parseNumero(valorDeslocamento);
    const pedagio = parseNumero(valorPedagio);
    const mao = parseNumero(valorMaoObra);
    const pecas = parseNumero(valorPecas);
    const almoco = parseNumero(gastosAlmoco);
    const desconto = parseNumero(valorDesconto);
    const passagem = parseNumero(valorPassagem);
    const aluguel = aluguelCarro ? parseNumero(valorAluguelCarro) : 0;
    return mao + pecas + desloc + pedagio + almoco + passagem + aluguel - desconto;
  }, [
    valorDeslocamento,
    valorPedagio,
    valorMaoObra,
    valorPecas,
    gastosAlmoco,
    valorDesconto,
    valorPassagem,
    aluguelCarro,
    valorAluguelCarro,
  ]);

  const handleCalcularDistancia = async () => {
    if (!enderecoOrigem) {
      setErro("Endereço de origem não configurado. Configure em Opções > Grupo.");
      return;
    }

    if (!enderecoDestino) {
      setErro("Informe o endereço de destino.");
      return;
    }

    setCalculandoDistancia(true);
    setErro("");
    setSucesso("");

    try {
      const resultadoIda = await calcularDistancia(grupoId, enderecoOrigem, enderecoDestino);
      const novoKmIda = Number(resultadoIda.distanceKm || 0);
      let novoKmVolta = 0;
      let totalKm = novoKmIda;

      if (idaVolta) {
        const resultadoVolta = await calcularDistancia(grupoId, enderecoDestino, enderecoOrigem);
        novoKmVolta = Number(resultadoVolta.distanceKm || 0);
        totalKm += novoKmVolta;
      }

      setKmIda(novoKmIda);
      setKmVolta(novoKmVolta);
      setKmRodados(String(totalKm));
      setSucesso(
        idaVolta
          ? `Ida: ${novoKmIda} km | Volta: ${novoKmVolta} km | Total: ${totalKm} km`
          : `Distância: ${novoKmIda} km`
      );

      try {
        const tollIda = await apiMapsTolls({ grupoId, origin: enderecoOrigem, destination: enderecoDestino });
        const valorIda = Number(tollIda?.tollValue || 0);
        setPedagioIda(valorIda);

        if (idaVolta) {
          const tollVolta = await apiMapsTolls({ grupoId, origin: enderecoDestino, destination: enderecoOrigem });
          setPedagioVolta(Number(tollVolta?.tollValue || 0));
        } else {
          setPedagioVolta(0);
        }
      } catch (tollErr) {
        console.warn("Falha ao calcular pedágio:", tollErr);
      }
    } catch (e) {
      console.error("Erro ao calcular distância:", e);
      setErro(e.message || "Erro ao calcular distância");
    } finally {
      setCalculandoDistancia(false);
    }
  };

  const handleSalvar = async (event) => {
    event.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuario?.grupoId) {
      setErro("Usuario sem grupoId. Faca login novamente.");
      return;
    }

    if (!clienteId || !academiaId) {
      setErro("Selecione cliente e academia, ou escolha uma visita avulsa.");
      return;
    }

    if (!status) {
      setErro("Informe o status da proposta.");
      return;
    }

    setSalvando(true);
    try {
      const clienteNome = clientesById[String(clienteId)]?.nome || "";
      const academiaNome = setoresById[String(academiaId)]?.nome || "";
      const payload = {
        grupoId: usuario.grupoId,
        sessaoId: visitaId || null,
        atendimentoId: atendimentoId || null,
        clienteId: clienteId || null,
        clienteNome,
        academiaId: academiaId || null,
        academiaNome,
        status,
        dataPagamento: dataPagamento || null,
        formaPagamento: formaPagamento || null,
        enderecoDestino: enderecoDestino || null,
        tipoViagem,
        quantidadeAjudantes: parseNumero(quantidadeAjudantes) || 0,
        gastosAlmoco: parseNumero(gastosAlmoco) || 0,
        previsaoDias: parseNumero(previsaoDias) || 0,
        kmRodados: parseNumero(kmRodados) || 0,
        kmIda: Number(kmIda || 0),
        kmVolta: Number(kmVolta || 0),
        idaVolta: !!idaVolta,
        valorDeslocamento: parseNumero(valorDeslocamento) || 0,
        valorPedagio: parseNumero(valorPedagio) || 0,
        pedagioIda: Number(pedagioIda || 0),
        pedagioVolta: Number(pedagioVolta || 0),
        valorMaoObra: parseNumero(valorMaoObra) || 0,
        valorPecas: parseNumero(valorPecas) || 0,
        valorDesconto: parseNumero(valorDesconto) || 0,
        valorPassagem: parseNumero(valorPassagem) || 0,
        aluguelCarro: !!aluguelCarro,
        valorAluguelCarro: parseNumero(valorAluguelCarro) || 0,
        valorTotal: valorTotalCalculado,
        observacoes: observacoes.trim() || null,
        osResumo: osItens,
        osIds: osItens.map((os) => os.osId).filter(Boolean),
        osCount: osItens.length,
        updatedAt: serverTimestamp(),
      };

      if (isEdicao) {
        await updateDoc(doc(db, COLLECTION_PROPOSTAS_OS, id), payload);
        setSucesso("Proposta atualizada com sucesso.");
      } else {
        const ref = await addDoc(collection(db, COLLECTION_PROPOSTAS_OS), {
          ...payload,
          createdAt: serverTimestamp(),
        });

        if (visitaId) {
          await updateDoc(doc(db, COLLECTION_SESSOES, visitaId), {
            propostaId: ref.id,
            propostaStatus: status,
            liberadaAgendamento: String(status).toUpperCase() === "APROVADA",
            enderecoDestino: enderecoDestino || null,
            tipoViagem,
            quantidadeAjudantes: parseNumero(quantidadeAjudantes) || 0,
            gastosAlmoco: parseNumero(gastosAlmoco) || 0,
            previsaoDias: parseNumero(previsaoDias) || 0,
            kmRodados: parseNumero(kmRodados) || 0,
            kmIda: Number(kmIda || 0),
            kmVolta: Number(kmVolta || 0),
            idaVolta: !!idaVolta,
            valorDeslocamento: parseNumero(valorDeslocamento) || 0,
            valorPedagio: parseNumero(valorPedagio) || 0,
            pedagioIda: Number(pedagioIda || 0),
            pedagioVolta: Number(pedagioVolta || 0),
            valorMaoObra: parseNumero(valorMaoObra) || 0,
            valorPecas: parseNumero(valorPecas) || 0,
            valorDesconto: parseNumero(valorDesconto) || 0,
            valorPassagem: parseNumero(valorPassagem) || 0,
            aluguelCarro: !!aluguelCarro,
            valorAluguelCarro: parseNumero(valorAluguelCarro) || 0,
            valorTotal: valorTotalCalculado,
            updatedAt: serverTimestamp(),
          });
        }

        setSucesso("Proposta criada com sucesso.");
      }

      if (isEdicao) {
        if (visitaId) {
          await updateDoc(doc(db, COLLECTION_SESSOES, visitaId), {
            propostaStatus: status,
            liberadaAgendamento: String(status).toUpperCase() === "APROVADA",
            enderecoDestino: enderecoDestino || null,
            tipoViagem,
            quantidadeAjudantes: parseNumero(quantidadeAjudantes) || 0,
            gastosAlmoco: parseNumero(gastosAlmoco) || 0,
            previsaoDias: parseNumero(previsaoDias) || 0,
            kmRodados: parseNumero(kmRodados) || 0,
            kmIda: Number(kmIda || 0),
            kmVolta: Number(kmVolta || 0),
            idaVolta: !!idaVolta,
            valorDeslocamento: parseNumero(valorDeslocamento) || 0,
            valorPedagio: parseNumero(valorPedagio) || 0,
            pedagioIda: Number(pedagioIda || 0),
            pedagioVolta: Number(pedagioVolta || 0),
            valorMaoObra: parseNumero(valorMaoObra) || 0,
            valorPecas: parseNumero(valorPecas) || 0,
            valorDesconto: parseNumero(valorDesconto) || 0,
            valorPassagem: parseNumero(valorPassagem) || 0,
            aluguelCarro: !!aluguelCarro,
            valorAluguelCarro: parseNumero(valorAluguelCarro) || 0,
            valorTotal: valorTotalCalculado,
            updatedAt: serverTimestamp(),
          });
        }
      }

      setTimeout(() => navigate("/financeiro/propostas-os"), 350);
    } catch (e) {
      console.error("Erro ao salvar proposta:", e);
      const msgErro = e?.message || "Erro ao salvar proposta de OS.";
      const detalhes = e?.code || "";
      setErro(`${msgErro}${detalhes ? ` (${detalhes})` : ""}`);
    } finally {
      setSalvando(false);
    }
  };

  const handleGerarPdf = async () => {
    if (!grupoId) {
      setErro("Usuário sem grupoId para gerar PDF.");
      return;
    }

    setErro("");
    setSucesso("");
    setGerandoPdf(true);
    try {
      const dadosPdf = {
        clienteNome: clientesById[String(clienteId)]?.nome || "",
        academiaNome: setoresById[String(academiaId)]?.nome || "",
        status,
        formaPagamento,
        dataPagamento,
        observacoes,
        kmRodados,
        valorMaoObra,
        valorPecas,
        valorDeslocamento,
        valorPedagio,
        gastosAlmoco,
        valorPassagem,
        valorAluguelCarro: aluguelCarro ? valorAluguelCarro : 0,
        valorDesconto,
        valorTotal: valorTotalCalculado,
        osItens,
      };

      await gerarPdfPropostaOs({
        grupoId,
        dados: dadosPdf,
      });
      setSucesso("PDF da proposta de OS gerado com sucesso.");
    } catch (e) {
      setErro(String(e?.message || e));
    } finally {
      setGerandoPdf(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!grupoId) {
      setErro("Usuário sem grupoId para enviar e-mail.");
      return;
    }

    const clienteAtual = clientesById[String(clienteId)] || null;
    const academiaAtual = setoresById[String(academiaId)] || null;
    const emailPadrao = String(
      academiaAtual?.emailFinanceiro
        || academiaAtual?.email
        || clienteAtual?.emailFinanceiro
        || clienteAtual?.email
        || ""
    ).trim();

    const toRaw = window.prompt("E-mail de destino", emailPadrao);
    if (toRaw == null) return;
    const to = String(toRaw || "").trim();
    if (!to) {
      setErro("Informe um e-mail de destino para enviar a proposta.");
      return;
    }

    const dadosPdf = {
      clienteNome: clienteAtual?.nome || "",
      academiaNome: academiaAtual?.nome || "",
      status,
      formaPagamento,
      dataPagamento,
      observacoes,
      kmRodados,
      valorMaoObra,
      valorPecas,
      valorDeslocamento,
      valorPedagio,
      gastosAlmoco,
      valorPassagem,
      valorAluguelCarro: aluguelCarro ? valorAluguelCarro : 0,
      valorDesconto,
      valorTotal: valorTotalCalculado,
      osItens,
    };

    setErro("");
    setSucesso("");
    setEnviandoEmail(true);
    try {
      const { blob, fileName } = await gerarPdfPropostaOs({
        grupoId,
        dados: dadosPdf,
        retornarBlob: true,
      });
      const pdfBase64 = await blobToBase64(blob);

      await apiEnviarPropostaEmail({
        grupoId,
        tipo: "os",
        to,
        subject: `Proposta de OS - ${dadosPdf.clienteNome || "Cliente"}`,
        fileName,
        pdfBase64,
        context: {
          clienteNome: dadosPdf.clienteNome,
          academiaNome: dadosPdf.academiaNome,
          tipoDocumento: "Proposta de Ordem de Serviço",
        },
      });

      setSucesso(`Proposta enviada por e-mail para ${to}.`);
    } catch (e) {
      setErro(String(e?.message || e));
    } finally {
      setEnviandoEmail(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo={isEdicao ? "Editar proposta OS" : "Nova proposta OS"}
      subtitulo="Financeiro / Propostas / Ordens de Servico"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>{isEdicao ? "Editar proposta" : "Nova proposta"}</h3>
          <p>Selecione a visita avulsa, preencha status e dados de pagamento.</p>
        </header>

        {carregandoRegistro ? (
          <p style={{ padding: 10 }}>Carregando proposta...</p>
        ) : (
          <form onSubmit={handleSalvar} className="pc-form">
            {erro && (
              <div className="pc-alert pc-alert-erro">{erro}</div>
            )}
            {sucesso && (
              <div className="pc-alert pc-alert-sucesso">{sucesso}</div>
            )}

            <div className="pc-form-grid">
              <label className="pc-form-group pc-form-group-full">
                Visita (OS Avulsa)
                <select
                  value={visitaId}
                  onChange={(e) => setVisitaId(e.target.value)}
                  className="pc-input"
                  disabled={carregando || isEdicao}
                >
                  <option value="">Sem visita (preencher cliente e academia manualmente)</option>
                  {visitas.map((v) => {
                    const clienteNome = clientesById[String(v.clienteId)]?.nome || "Cliente";
                    const academiaNome = setoresById[String(v.academiaId)]?.nome || "Academia";
                    return (
                      <option key={v.id} value={v.id}>
                        {clienteNome} • {academiaNome} • {v.id.slice(0, 6)}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="pc-form-group">
                Cliente
                <select
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  className="pc-input"
                  disabled={carregando}
                >
                  <option value="">Selecione</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || c.id}
                    </option>
                  ))}
                </select>
              </label>

              <label className="pc-form-group">
                Academia
                <select
                  value={academiaId}
                  onChange={(e) => setAcademiaId(e.target.value)}
                  className="pc-input"
                  disabled={carregando || !clienteId}
                >
                  <option value="">Selecione</option>
                  {academiasFiltradas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nome || a.codigo || a.id}
                    </option>
                  ))}
                </select>
              </label>

              <label className="pc-form-group">
                Status
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="pc-input">
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="pc-form-group">
                Data de pagamento
                <input type="date" value={dataPagamento} onChange={(e) => setDataPagamento(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Forma de pagamento
                <select value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)} className="pc-input">
                  <option value="">Selecione</option>
                  {FORMAS_PAGAMENTO.map((forma) => (
                    <option key={forma} value={forma}>
                      {forma}
                    </option>
                  ))}
                </select>
              </label>

              <label className="pc-form-group pc-form-group-full">
                Observacoes
                <textarea rows={3} className="pc-input" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
              </label>

              <div style={{ gridColumn: "1 / -1", fontWeight: 700, opacity: 0.9, marginTop: 6 }}>
                Endereço e Navegação
              </div>

              <label className="pc-form-group pc-form-group-full">
                Endereço de destino (academia)
                <input type="text" value={enderecoDestino} onChange={(e) => setEnderecoDestino(e.target.value)} className="pc-input" />
              </label>

              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="pc-btn-secundario"
                  onClick={handleCalcularDistancia}
                  disabled={!enderecoDestino || !enderecoOrigem || calculandoDistancia}
                >
                  {calculandoDistancia ? "Calculando..." : "📍 Calcular distância"}
                </button>

                {enderecoDestino && (
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => {
                      const url = gerarUrlGoogleMaps(enderecoDestino, enderecoOrigem || null);
                      window.open(url, "_blank");
                    }}
                  >
                    🗺️ Abrir no Google Maps
                  </button>
                )}

                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={idaVolta}
                    onChange={(e) => setIdaVolta(e.target.checked)}
                    style={{ margin: 0, width: 18, height: 18, cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 13, cursor: "pointer" }}>Ida e Volta</span>
                </label>

                {!enderecoOrigem && (
                  <div style={{ fontSize: 12, color: "#f59e0b", padding: 4 }}>
                    ⚠️ Configure o endereço de origem em Opções
                  </div>
                )}
              </div>

              <div style={{ gridColumn: "1 / -1", fontWeight: 700, opacity: 0.9, marginTop: 8 }}>
                Financeiro • Deslocamento
              </div>

              <label className="pc-form-group">
                Km rodados
                <input type="number" min="0" step="0.1" value={kmRodados} onChange={(e) => setKmRodados(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Valor por km (R$)
                <input type="text" readOnly value={formatNumero(valorKmDeslocamento, 2)} className="pc-input" style={{ opacity: 0.7, cursor: "not-allowed" }} />
                <small style={{ opacity: 0.6, marginTop: 2, display: "block" }}>Configurado em Opções / Grupo</small>
              </label>

              <label className="pc-form-group">
                Valor deslocamento (R$)
                <input type="text" value={valorDeslocamento} onChange={(e) => setValorDeslocamento(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Pedagio (R$)
                <input type="number" min="0" step="0.01" value={valorPedagio} onChange={(e) => setValorPedagio(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Valor mao de obra (R$)
                <input type="number" min="0" step="0.01" value={valorMaoObra} onChange={(e) => setValorMaoObra(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Valor pecas (R$)
                <input type="number" min="0" step="0.01" value={valorPecas} onChange={(e) => setValorPecas(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Desconto (R$)
                <input type="number" min="0" step="0.01" value={valorDesconto} onChange={(e) => setValorDesconto(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Tipo de viagem
                <select value={tipoViagem} onChange={(e) => setTipoViagem(e.target.value)} className="pc-input">
                  <option value="CARRO">Carro</option>
                  <option value="AVIAO">Avião</option>
                </select>
              </label>

              {tipoViagem === "AVIAO" && (
                <>
                  <label className="pc-form-group">
                    Valor passagem (R$)
                    <input type="number" min="0" step="0.01" value={valorPassagem} onChange={(e) => setValorPassagem(e.target.value)} className="pc-input" />
                  </label>

                  <label className="pc-form-group">
                    Aluguel de carro
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={aluguelCarro}
                        onChange={(e) => setAluguelCarro(e.target.checked)}
                        style={{ margin: 0, width: 18, height: 18, cursor: "pointer" }}
                      />
                      <span style={{ fontSize: 13 }}>Incluir aluguel</span>
                    </div>
                  </label>

                  {aluguelCarro && (
                    <label className="pc-form-group">
                      Valor aluguel (R$)
                      <input type="number" min="0" step="0.01" value={valorAluguelCarro} onChange={(e) => setValorAluguelCarro(e.target.value)} className="pc-input" />
                    </label>
                  )}
                </>
              )}

              <label className="pc-form-group">
                Quantidade de ajudantes
                <input type="number" min="0" step="1" value={quantidadeAjudantes} onChange={(e) => setQuantidadeAjudantes(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Gastos com Almoco (R$)
                <input type="number" min="0" step="0.01" value={gastosAlmoco} onChange={(e) => setGastosAlmoco(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Previsão de dias
                <input type="number" min="1" step="1" value={previsaoDias} onChange={(e) => setPrevisaoDias(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group pc-form-group-full">
                Valor total (R$)
                <input type="text" readOnly className="pc-input" value={formatNumero(valorTotalCalculado)} />
              </label>

              <div className="pc-form-group pc-form-group-full">
                <strong>OS vinculadas</strong>
                <div style={{ marginTop: 6, background: "rgba(15,23,42,0.35)", borderRadius: 8, padding: 10 }}>
                  {osItens.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {osItens.map((os) => (
                        <li key={os.osId || os.osCodigo}>
                          {os.osCodigo ? `#${os.osCodigo} - ` : ""}
                          {os.osTitulo || os.osTipoServico || os.osId}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div>Nenhuma OS vinculada.</div>
                  )}
                </div>
                <div style={{ marginTop: 6, fontWeight: 600 }}>Total OS: {osItens.length}</div>
                <div style={{ marginTop: 6, opacity: 0.7 }}>Valor sugerido: R$ {formatMoney(valorTotalCalculado)}</div>
              </div>
            </div>

            <div className="pc-form-actions">
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleGerarPdf}
                disabled={salvando || carregando || gerandoPdf || enviandoEmail}
              >
                {gerandoPdf ? "Gerando PDF..." : "Gerar PDF"}
              </button>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleEnviarEmail}
                disabled={salvando || carregando || gerandoPdf || enviandoEmail}
              >
                {enviandoEmail ? "Enviando e-mail..." : "Enviar por e-mail"}
              </button>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/financeiro/propostas-os")}
                disabled={salvando || enviandoEmail}
              >
                Voltar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={salvando || carregando || enviandoEmail}>
                {salvando ? "Salvando..." : isEdicao ? "Salvar alteracoes" : "Salvar proposta"}
              </button>
            </div>
          </form>
        )}
      </section>
    </LayoutDesktop>
  );
}
